/*
 * 串口参数。
 *
 * 字段名与取值**逐字照搬 Web Serial 规范**（见 `src/types/web-serial.d.ts`），
 * 不做重命名也不做「更好懂」的封装 —— 这样 `port.open(options)` 可以直接把本类实例传进去，
 * 中间没有一层需要维护的映射（原型里 `flowControl` 用 boolean 建模，就得来回翻译）。
 *
 * readonly 的取值表放这里而不是组件里：配置页与调试页都要用，
 * 且它们就是这份类型本身的定义域，跟着类型走最不容易漂移。
 */

export type UartParity = 'none' | 'even' | 'odd';
export type UartFlowControl = 'none' | 'hardware';
/** 规范只允许 7 或 8。 */
export type UartDataBits = 7 | 8;
/** 规范只允许 1 或 2 —— **没有 1.5**，写 1.5 会在 `open()` 时抛错。 */
export type UartStopBits = 1 | 2;

/**
 * 一份完整的串口参数。
 *
 * 默认值即规范默认值：115200 8-N-1、无流控、缓冲区 255。
 */
export class UartOptions {
  baudRate: number = 115200;
  dataBits: UartDataBits = 8;
  stopBits: UartStopBits = 1;
  parity: UartParity = 'none';
  flowControl: UartFlowControl = 'none';
  /** 读写缓冲区字节数。规范默认 255，调大可减少高波特率下的丢包。 */
  bufferSize: number = 255;
}

/** 波特率常用档位，配置页的下拉就列这些，另给一个「自定义」入口。 */
export const BAUD_RATE_PRESETS: readonly number[] = [
  300, 600, 1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200, 230400, 460800, 921600,
];

export const UART_DATA_BITS_OPTIONS: readonly UartDataBits[] = [8, 7];
export const UART_STOP_BITS_OPTIONS: readonly UartStopBits[] = [1, 2];

/** 缓冲区大小的可填范围。上限取 1 MiB —— 再大对串口没有意义，只会白占内存。 */
export const UART_BUFFER_SIZE_MIN = 16;
export const UART_BUFFER_SIZE_MAX = 1024 * 1024;

/**
 * 下拉选项。`label` 是 i18n key（中文原文），模板里过一遍 `| translate`，
 * 与仓库其它页面同一个口径。
 */
export const UART_PARITY_OPTIONS: readonly { label: string; value: UartParity }[] = [
  { label: '无校验', value: 'none' },
  { label: '偶校验', value: 'even' },
  { label: '奇校验', value: 'odd' },
];

export const UART_FLOW_CONTROL_OPTIONS: readonly { label: string; value: UartFlowControl }[] = [
  { label: '无流控', value: 'none' },
  { label: '硬件流控', value: 'hardware' },
];

/**
 * 两组参数是否完全一致。
 *
 * 用于判断「端口打开时用的参数」与「当前界面上填的参数」有没有分叉 ——
 * 分叉了就得重新连接才生效（Web Serial 不支持在已打开的端口上改参数），
 * 界面要据此提示用户。
 *
 * 逐字段比而不是比 JSON：后者会受键顺序影响，一个重新排过键的对象明明内容一样却判为不同。
 */
export function sameUartOptions(a: UartOptions, b: UartOptions): boolean {
  return (
    a.baudRate === b.baudRate &&
    a.dataBits === b.dataBits &&
    a.stopBits === b.stopBits &&
    a.parity === b.parity &&
    a.flowControl === b.flowControl &&
    a.bufferSize === b.bufferSize
  );
}
